import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { athleteAPI, stripeAPI } from '../../services/api';
import { Athlete } from '../../types/mongodb';
import { AiFillHome } from 'react-icons/ai';
import { FiInstagram } from 'react-icons/fi';
import {
  WelcomeContainer,
  Header,
  HeaderContainer,
  Logo,
  LogoSJ,
  LogoMate,
  NavLinks,
  NavLink,
  NavHomeIcon,
  NavButtons,
  SignInButton,
  PrimaryButton,
  DashboardButton,
  DashboardBadge,
  PricingGrid,
  PricingCard,
  PricingBadge,
  PricingTitle,
  PricingPrice,
  PricingPeriod,
  PricingFeatures,
  PricingFeature,
  PricingDiscountContainer,
  PricingOriginalPrice,
  PricingDiscountPrice,
  PricingDiscountBadgeTop,
  Section,
  SectionTitle,
  SectionSubtitle,
  Footer,
  FooterContent,
  FooterSection,
  FooterLogo,
  FooterLogoSJ,
  FooterLogoMate,
  FooterTitle,
  FooterLink,
  FooterBottom,
  FooterLine,
  FooterText,
  FooterSocialLinks,
  FooterSocialLink,
} from '../WelcomeScreen/styles';
import styled from 'styled-components';

// Type assertion helper for icons
const Icon = ({ component: Component, ...props }: { component: any; [key: string]: any }) => {
  return <Component {...props} />;
};

const PricingContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: var(--bg-primary);
  position: relative;
  overflow-x: hidden;
  padding-top: 6rem;
`;

const PricingSection = styled.section`
  width: 100%;
  padding: 6rem 4rem;
  margin: 0 auto;

  @media (max-width: 1400px) {
    padding: 6rem 3rem;
  }

  @media (max-width: 1200px) {
    padding: 4rem 2rem;
  }

  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 3rem 1rem;
  }
`;

const BillingToggle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0 3rem;
  font-family: 'Poppins', sans-serif;
`;

const ToggleButton = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  border: 2px solid ${props => props.active ? 'var(--accent-orange)' : 'var(--card-border)'};
  background: ${props => props.active ? 'var(--accent-orange)' : 'var(--bg-primary)'};
  color: ${props => props.active ? 'var(--text-light)' : 'var(--text-dark)'};
  border-radius: var(--radius-full);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Poppins', sans-serif;

  &:hover {
    border-color: var(--accent-orange);
    transform: translateY(-2px);
  }
`;

const YearlyBadge = styled.span`
  background: var(--accent-orange);
  color: var(--text-light);
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  font-weight: 700;
  margin-left: 0.5rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const ModalContent = styled.div`
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: var(--shadow-lg);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
`;

const AthleteList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin: 1.5rem 0;
  max-height: 400px;
  overflow-y: auto;
`;

const AthleteItem = styled.label`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 2px solid var(--card-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Poppins', sans-serif;

  &:hover {
    border-color: var(--accent-orange);
    background: rgba(255, 150, 0, 0.05);
  }

  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    cursor: pointer;
  }

  span {
    flex: 1;
    font-weight: 500;
    color: var(--text-dark);
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const ModalCancelButton = styled.button`
  flex: 1;
  padding: 0.4rem 1rem;
  font-size: 0.75rem;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  font-weight: 800;
  letter-spacing: -0.02em;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;
  background: #1a1a1a;
  border: 2px solid #1a1a1a;
  color: #fff;

  &:hover {
    background: #2d2d2d;
    border-color: #2d2d2d;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  font-family: 'Poppins', sans-serif;
  white-space: pre-line;
  line-height: 1.5;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 150, 0, 0.3);
  border-radius: 50%;
  border-top-color: var(--accent-orange);
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// Set to true to show discounted prices, false to show full prices
// Change this value to switch between pricing modes
const USE_DISCOUNTED_PRICES = true;

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const showDiscountedPrices = USE_DISCOUNTED_PRICES; // Controlled by constant above
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [selectedAthletes, setSelectedAthletes] = useState<string[]>([]);
  const [showAthleteModal, setShowAthleteModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'hs60' | 'hs90' | 'hs130' | 'hs240' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    if (user?.role === 'coach') {
      loadAthletes();
    }
  }, [user]);

  // After sign-in from pricing: complete pending checkout and redirect to Stripe
  useEffect(() => {
    if (!user) return;
    const raw = sessionStorage.getItem('sjMate_pendingCheckout');
    if (!raw) return;
    let parsed: { plan: 'hs60' | 'hs90' | 'hs130' | 'hs240'; billingPeriod: 'monthly' | 'yearly'; athleteIds: string[] };
    try {
      parsed = JSON.parse(raw);
    } catch {
      sessionStorage.removeItem('sjMate_pendingCheckout');
      return;
    }
    const runPendingCheckout = async () => {
      sessionStorage.removeItem('sjMate_pendingCheckout');
      try {
        setIsLoading(true);
        setError(null);
        const response =
          user.role === 'coach'
            ? await stripeAPI.createCoachSetupSession({
                plan: parsed.plan,
                billingPeriod: parsed.billingPeriod,
                athleteIds: parsed.athleteIds,
                useDiscount: showDiscountedPrices
              })
            : await stripeAPI.createCheckoutSession({
                plan: parsed.plan,
                billingPeriod: parsed.billingPeriod,
                useDiscount: showDiscountedPrices
              });
        if (response?.url) {
          window.location.href = response.url;
        } else {
          setError('No checkout URL received. Please try again.');
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to start checkout. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    runPendingCheckout();
  }, [user, showDiscountedPrices]);

  const loadAthletes = async () => {
    try {
      const data = await athleteAPI.getAll();
      setAthletes(data);
    } catch (err) {
      console.error('Error loading athletes:', err);
    }
  };

  const handlePlanSelect = (plan: 'hs60' | 'hs90' | 'hs130' | 'hs240') => {
    if (plan === 'hs240') return; // HS240 / AI Mate not available for purchase yet
    if (user?.role === 'coach') {
      setSelectedPlan(plan);
      setSelectedAthletes([]);
      setShowAthleteModal(true);
    } else {
      initiateCheckout(plan, []);
    }
  };

  const handleAthleteToggle = (athleteId: string) => {
    setSelectedAthletes(prev =>
      prev.includes(athleteId)
        ? prev.filter(id => id !== athleteId)
        : [...prev, athleteId]
    );
  };

  const handleConfirmAthletes = () => {
    if (selectedAthletes.length === 0) {
      setError('Please select at least one athlete');
      return;
    }
    if (selectedPlan) {
      initiateCheckout(selectedPlan, selectedAthletes);
    }
  };

  const PENDING_CHECKOUT_KEY = 'sjMate_pendingCheckout';

  const initiateCheckout = async (plan: 'hs60' | 'hs90' | 'hs130' | 'hs240', athleteIds: string[]) => {
    // Check if user is authenticated
    if (!user) {
      sessionStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify({ plan, billingPeriod, athleteIds }));
      navigate(`/signin?returnUrl=${encodeURIComponent('/pricing')}`);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response =
        user.role === 'coach'
          ? await stripeAPI.createCoachSetupSession({
              plan,
              billingPeriod,
              athleteIds,
              useDiscount: showDiscountedPrices
            })
          : await stripeAPI.createCheckoutSession({
              plan,
              billingPeriod,
              useDiscount: showDiscountedPrices
            });

      // Show downgrade warnings if any (but still proceed to checkout)
      if (response.warnings && response.warnings.length > 0) {
        const warningMessage = response.warnings.join('\n');
        // Show warning but allow user to proceed
        const proceed = window.confirm(
          `${warningMessage}\n\nDo you want to proceed with the downgrade?`
        );
        if (!proceed) {
          setIsLoading(false);
          return;
        }
      }

      // Redirect to Stripe Checkout
      if (response.url) {
        window.location.href = response.url;
      } else {
        throw new Error('No checkout URL received from server');
      }
    } catch (err: any) {
      // Handle authentication errors - store checkout intent so after sign-in we go to Stripe
      if (err.response?.status === 401 || err.response?.status === 403) {
        sessionStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify({ plan, billingPeriod, athleteIds }));
        navigate(`/signin?returnUrl=${encodeURIComponent('/pricing')}`);
        return;
      }
      
      // Handle validation errors (including downgrade warnings)
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        const errorMessages = err.response.data.errors.join('\n');
        setError(errorMessages);
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to start checkout. Please try again.');
      }
      setIsLoading(false);
    }
  };

  const getDisplayPrice = (plan: 'hs60' | 'hs90' | 'hs130' | 'hs240'): { original: number; discounted: number; discountPercent: number; current: number } => {
    const prices = {
      hs60: {
        monthly: { original: 4.90, discounted: 4.41, discountPercent: 10 },
        yearly: { original: 52.92, discounted: 47.62, discountPercent: 10 }
      },
      hs90: {
        monthly: { original: 11.90, discounted: 9.52, discountPercent: 20 },
        yearly: { original: 128.52, discounted: 102.81, discountPercent: 20 }
      },
      hs130: {
        monthly: { original: 17.90, discounted: 12.53, discountPercent: 30 },
        yearly: { original: 193.32, discounted: 135.32, discountPercent: 30 }
      },
      hs240: {
        monthly: { original: 24.90, discounted: 19.92, discountPercent: 20 },
        yearly: { original: 268.92, discounted: 215.14, discountPercent: 20 }
      }
    };
    const planPrices = prices[plan][billingPeriod];
    return {
      ...planPrices,
      current: showDiscountedPrices ? planPrices.discounted : planPrices.original
    };
  };

  const pricingPlans = [
    {
      id: 'free' as const,
      title: 'FREE',
      features: [
        { name: 'Home', available: true },
        { name: 'Athletes', available: true },
        { name: 'Training Sessions', available: true },
        { name: 'Competition Sessions', available: false },
        { name: 'Equipment', available: false },
        { name: 'Training Planner', available: false },
        { name: 'Banana Cup', available: false },
        { name: 'Analytics', available: false },
        { name: 'AI Chat', available: false },
      ]
    },
    {
      id: 'hs60' as const,
      title: 'HS60',
      discountPercent: 10,
      features: [
        { name: 'Home', available: true },
        { name: 'Athletes', available: true },
        { name: 'Training Sessions', available: true },
        { name: 'Competition Sessions', available: true },
        { name: 'Equipment', available: true },
        { name: 'Training Planner', available: false },
        { name: 'Banana Cup', available: false },
        { name: 'Analytics', available: false },
        { name: 'AI Chat', available: false },
      ]
    },
    {
      id: 'hs90' as const,
      title: 'HS90',
      discountPercent: 20,
      features: [
        { name: 'Home', available: true },
        { name: 'Athletes', available: true },
        { name: 'Training Sessions', available: true },
        { name: 'Competition Sessions', available: true },
        { name: 'Equipment', available: true },
        { name: 'Training Planner', available: true },
        { name: 'Banana Cup', available: true },
        { name: 'Analytics', available: false },
        { name: 'AI Chat', available: false },
      ]
    },
    {
      id: 'hs130' as const,
      title: 'HS130',
      discountPercent: 30,
      featured: true,
      features: [
        { name: 'Home', available: true },
        { name: 'Athletes', available: true },
        { name: 'Training Sessions', available: true },
        { name: 'Competition Sessions', available: true },
        { name: 'Equipment', available: true },
        { name: 'Training Planner', available: true },
        { name: 'Banana Cup', available: true },
        { name: 'Analytics', available: true },
        { name: 'AI Chat', available: false },
      ]
    },
    {
      id: 'hs240' as const,
      title: 'HS240',
      discountPercent: 20,
      features: [
        { name: 'Home', available: true },
        { name: 'Athletes', available: true },
        { name: 'Training Sessions', available: true },
        { name: 'Competition Sessions', available: true },
        { name: 'Equipment', available: true },
        { name: 'Training Planner', available: true },
        { name: 'Banana Cup', available: true },
        { name: 'Analytics', available: true },
        { name: 'AI Chat', available: true },
      ]
    }
  ];

  const handleSignIn = () => {
    navigate('/signin');
  };

  const handleSignUp = () => {
    navigate('/signup');
  };

  const handleDashboard = () => {
    if (user?.role === 'coach') {
      navigate('/coach/home');
    } else if (user?.role === 'athlete') {
      navigate('/jumper/home');
    }
  };

  return (
    <PricingContainer>
      <Header>
        <HeaderContainer>
          <Logo onClick={() => navigate('/')}>
            <LogoSJ>sj</LogoSJ>
            <LogoMate>Mate</LogoMate>
          </Logo>
          <NavLinks>
            <NavHomeIcon onClick={() => navigate('/')}>
              <Icon component={AiFillHome} />
            </NavHomeIcon>
            <NavLink onClick={() => navigate('/#features')}>Features</NavLink>
            <NavLink onClick={() => navigate('/#faq')}>FAQ</NavLink>
            <NavLink onClick={() => navigate('/pricing')}>Pricing</NavLink>
          </NavLinks>
          <NavButtons>
            {user ? (
              <DashboardButton onClick={handleDashboard}>
                Dashboard
                <DashboardBadge>{user.firstName?.charAt(0).toUpperCase() || 'U'}</DashboardBadge>
              </DashboardButton>
            ) : (
              <>
                <SignInButton onClick={handleSignIn}>Sign In</SignInButton>
                <PrimaryButton onClick={handleSignUp}>Start For Free</PrimaryButton>
              </>
            )}
          </NavButtons>
        </HeaderContainer>
      </Header>

      <PricingSection>
        <SectionTitle>Simple, Transparent Pricing</SectionTitle>
        <SectionSubtitle style={{ 
          marginBottom: '1rem', 
          marginTop: '1rem', 
          fontSize: '1rem',
          color: 'var(--text-gray)',
          fontWeight: 400,
          lineHeight: 1.6,
          maxWidth: '800px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          Choose the perfect plan for your needs. Athletes can subscribe for themselves, while coaches can purchase subscriptions for their athletes. All plans unlock features based on the selected tier, and you can upgrade anytime.
        </SectionSubtitle>
        {showDiscountedPrices && (
          <SectionSubtitle style={{ marginBottom: '1.5rem', marginTop: '0.5rem', color: 'var(--accent-orange)', fontWeight: 600, fontSize: '0.95rem' }}>
            Early Bird ⚡ Limited Discount: Become one of our first customers and use our exclusive discounts!
          </SectionSubtitle>
        )}

        <BillingToggle>
          <ToggleButton active={billingPeriod === 'monthly'} onClick={() => setBillingPeriod('monthly')}>
            Monthly
          </ToggleButton>
          <ToggleButton active={billingPeriod === 'yearly'} onClick={() => setBillingPeriod('yearly')}>
            Yearly
            <YearlyBadge>Save 10%</YearlyBadge>
          </ToggleButton>
        </BillingToggle>

        <PricingGrid>
          {pricingPlans.map((plan) => {
            if (plan.id === 'free') {
              return (
                <PricingCard key={plan.id}>
                  <PricingTitle>{plan.title}</PricingTitle>
                  <PricingDiscountContainer>
                    <PricingOriginalPrice style={{ visibility: 'hidden' }}>&nbsp;</PricingOriginalPrice>
                    <PricingPrice>FREE</PricingPrice>
                  </PricingDiscountContainer>
                  <PricingFeatures>
                    {plan.features.map((feature, idx) => (
                      <PricingFeature key={idx} unavailable={!feature.available}>
                        {feature.name}
                      </PricingFeature>
                    ))}
                  </PricingFeatures>
                  <PrimaryButton 
                    onClick={() => {
                      if (user) {
                        navigate(user.role === 'coach' ? '/coach/home' : '/jumper/home');
                      } else {
                        navigate('/signup');
                      }
                    }} 
                    style={{ width: '100%', marginTop: 'auto' }}
                  >
                    Start Free
                  </PrimaryButton>
                </PricingCard>
              );
            }

            const isHs240ComingSoon = plan.id === 'hs240';
            const prices = getDisplayPrice(plan.id);
            const periodText = billingPeriod === 'monthly' ? '/month/per athlete' : '/year/per athlete';

            return (
              <PricingCard key={plan.id} featured={plan.featured}>
                {plan.featured && <PricingBadge>MOST<br/>VALUED</PricingBadge>}
                {showDiscountedPrices && !isHs240ComingSoon && <PricingDiscountBadgeTop>{plan.discountPercent}% OFF</PricingDiscountBadgeTop>}
                <PricingTitle>{plan.title}</PricingTitle>
                <PricingDiscountContainer style={isHs240ComingSoon ? { minHeight: '7.25rem' } : undefined}>
                  {isHs240ComingSoon ? (
                    <PricingDiscountPrice style={{ flexDirection: 'column', gap: '0.25rem', fontSize: '1.2rem', letterSpacing: '-0.04em', marginTop: '1rem' }}>
                      COMING SOON
                    </PricingDiscountPrice>
                  ) : (
                    <>
                      {showDiscountedPrices && (
                        <PricingOriginalPrice>€{prices.original.toFixed(2)}</PricingOriginalPrice>
                      )}
                      <PricingDiscountPrice style={{ flexDirection: 'column', gap: '0.25rem' }}>
                        €{prices.current.toFixed(2)}
                        <PricingPeriod style={{ fontSize: '1rem', marginTop: '0.25rem' }}>{periodText}</PricingPeriod>
                      </PricingDiscountPrice>
                    </>
                  )}
                </PricingDiscountContainer>
                <PricingFeatures>
                  {plan.features.map((feature, idx) => (
                    <PricingFeature key={idx} unavailable={!feature.available}>
                      {feature.name}
                    </PricingFeature>
                  ))}
                </PricingFeatures>
                {isHs240ComingSoon ? (
                  <div style={{ width: '100%', marginTop: 'auto', textAlign: 'center', fontWeight: 600, color: 'var(--text-gray)', fontFamily: 'Poppins', padding: '1rem', fontSize: '0.7rem', letterSpacing: '-0.03em' }}>
                    COMING SOON
                  </div>
                ) : (
                  <PrimaryButton
                    onClick={() => handlePlanSelect(plan.id)}
                    disabled={isLoading}
                    style={{ width: '100%', marginTop: 'auto', opacity: isLoading ? 0.7 : 1 }}
                  >
                    {isLoading ? <LoadingSpinner /> : `Get ${plan.title}`}
                  </PrimaryButton>
                )}
              </PricingCard>
            );
          })}
        </PricingGrid>

        {error && <ErrorMessage style={{ textAlign: 'center', marginTop: '2rem' }}>{error}</ErrorMessage>}
      </PricingSection>

      {showAthleteModal && (
        <ModalOverlay onClick={() => setShowAthleteModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Select Athletes for {selectedPlan?.toUpperCase()} Plan</ModalTitle>
            <p style={{ color: 'var(--text-gray)', marginBottom: '1rem', fontFamily: 'Poppins' }}>
              Choose which athletes should have access to this plan:
            </p>
            <AthleteList>
              {athletes.map((athlete) => (
                <AthleteItem key={athlete._id}>
                  <input
                    type="checkbox"
                    checked={selectedAthletes.includes(athlete._id)}
                    onChange={() => handleAthleteToggle(athlete._id)}
                  />
                  <span>{athlete.firstName} {athlete.lastName}</span>
                </AthleteItem>
              ))}
            </AthleteList>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <ModalButtons>
              <ModalCancelButton
                onClick={() => {
                  setShowAthleteModal(false);
                  setError(null);
                }}
              >
                Cancel
              </ModalCancelButton>
              <PrimaryButton
                onClick={handleConfirmAthletes}
                disabled={isLoading}
                style={{ flex: 1 }}
              >
                {isLoading ? <LoadingSpinner /> : 'Continue to Checkout'}
              </PrimaryButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Footer */}
      <Footer>
        <FooterContent>
          <FooterSection>
            <FooterLogo>
              <FooterLogoSJ>sj</FooterLogoSJ>
              <FooterLogoMate>Mate</FooterLogoMate>
            </FooterLogo>
            <div style={{ marginTop: '2.5rem' }}>
              <FooterTitle>About sjMate</FooterTitle>
              <FooterLink 
                href="/#features" 
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/#features');
                }}
                style={{ cursor: 'pointer' }}
              >
                Features
              </FooterLink>
              <FooterLink 
                href="/pricing" 
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/pricing');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                style={{ cursor: 'pointer' }}
              >
                Pricing
              </FooterLink>
              <FooterLink 
                href="/how-to-start" 
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/how-to-start');
                }}
                style={{ cursor: 'pointer' }}
              >
                How It Works
              </FooterLink>
            </div>
          </FooterSection>
          <FooterSection>
            <div style={{ marginTop: '2.5rem' }}>
              <FooterTitle>Legal</FooterTitle>
              <FooterLink href="/privacy-policy">Privacy Policy</FooterLink>
              <FooterLink href="/terms-of-service">Terms of Service</FooterLink>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('sjmate-open-cookie-settings'))}
                style={{ background: 'none', border: 'none', padding: 0, marginTop: '0.15rem', font: 'inherit', color: 'var(--text-muted)', textDecoration: 'none', cursor: 'pointer', fontSize: '0.95rem', fontWeight: 600, display: 'block', lineHeight: 1.4 }}
              >
                Cookie settings
              </button>
            </div>
          </FooterSection>
          <FooterSection>
            <div style={{ marginTop: '2.5rem' }}>
              <FooterTitle>Contact</FooterTitle>
              <FooterLink href="mailto:skijumpingmate@gmail.com">skijumpingmate@gmail.com</FooterLink>
              <FooterLink href="https://instagram.com/sjmate" target="_blank" rel="noopener noreferrer">
                <Icon component={FiInstagram} style={{ display: 'inline-block', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Instagram
              </FooterLink>
            </div>
          </FooterSection>
        </FooterContent>
        <FooterBottom>
          <FooterText>
            Created and designed in 2026 by <b>z-epic</b>
          </FooterText>
        </FooterBottom>
        <FooterLine />
      </Footer>
    </PricingContainer>
  );
};

export default Pricing;

